/*
 * Copyright (c) 2020 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { Directive, Input, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

@Directive({
  host: { class: 'irian-custom-cva' },
})
export abstract class AbstractControlValueAccessorControl
  implements ControlValueAccessor, OnDestroy, Validator
{
  form: UntypedFormControl | UntypedFormArray | UntypedFormGroup | undefined = undefined;
  onChange: any | undefined = undefined;
  onTouched: any | undefined = undefined;
  protected onDestroy: Subject<boolean> = new Subject<boolean>();

  protected constructor() {
    this.createForm();
  }

  private _dirty: any;

  get dirty() {
    return this._dirty;
  }

  @Input() set dirty(value) {
    this._dirty = value;
    if (value) {
      setTimeout(() => {
        this.markAllAsDirty(this.form);
      }, 0)
    }
  }

  private _touched: any;

  get touched() {
    return this._touched;
  }

  @Input() set touched(value) {
    this._touched = value;
    if (value) {
      setTimeout(() => {
        this.form?.markAllAsTouched();
      }, 0)
    }
  }

  get formGroupErrors() {
    let errors: any = [];
    if (this.form && this.form['controls']) {
      errors = Object.keys(this.form['controls'])
        .map((key) => this.form['controls'][key].errors)
        .filter((item) => item);
    }

    return Object.assign(
      {},
      this.form?.errors ? { formGroup: this.form.errors } : {},
      errors
    );
  }

  abstract writeValue(obj: any): void;

  registerOnChange(fn: any): void {
    this.onChange = fn;

    if (this.form) {
      this.form.valueChanges
        .pipe(
          takeUntil(this.onDestroy),
          debounceTime(50),
          map((res) => this.onChangeCallback(res))
        )
        .subscribe((res) => {
          if (this.onChange) {
            this.onChange(res);
          }
        });
    }
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form?.disable();
    } else {
      this.form?.enable();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(true);

    (this.form as any) = undefined;
  }

  validate(): ValidationErrors | null {
    if (!this.form || this.form.valid) {
      return null;
    }
    return this.formGroupErrors;
  }

  markAllAsDirty(form: any) {
    Object.keys(form.controls).forEach((key) => {
      if (
        form.controls[key] instanceof UntypedFormGroup ||
        form.controls[key] instanceof UntypedFormArray
      ) {
        this.markAllAsDirty(form.controls[key]);
      }
      form.controls[key].markAsDirty();
    });
  }

  protected abstract createForm(): void;

  protected onChangeCallback(res: any) {
    return res;
  }
}
